import { clientStaffMark, clientStaffNotice } from '@steward-app/steward-types';
import { earliestStaffAt, markDate, questionLabel, type StaffMarks } from './staffMarks';

/**
 * What the CLIENT sees of answers their advisor entered (docs/43 §4, D8, D9; docs/40 §4.7;
 * crossmarc/steward-app#2538). Wording is Matt's (the notice, 2026-10-27) and provisional (the mark).
 * The client is never told which advisor: the read carries no `by`.
 */

/**
 * The notice at the top of the form, shown while any staff-entered answer remains. Its date is
 * when the client agreed to staff entry (`staffEntryConsentAt` on the client's read, appcore
 * docs/06 §34), else the first staff answer's.
 */
export function ClientStaffNotice({ firmName, marks, since }: {
  firmName: string | null | undefined; marks: StaffMarks; since?: string | null | undefined;
}) {
  const earliest = earliestStaffAt(marks);
  if (earliest === null) return null;
  const first = since || earliest;
  return (
    <p role="note" data-testid="client-staff-notice"
      className="mt-0 mb-5 rounded-token border border-line bg-cream/60 px-4 py-3 text-[13px] leading-relaxed text-navy font-sans">
      {clientStaffNotice(firmName, markDate(first))}
    </p>
  );
}

/**
 * The marks for one part of the form: one line per question (or list row) the advisor entered,
 * "Entered by your advisor, {date}", with the question it is on. `inPart` picks the marks this
 * part draws.
 */
export function ClientStaffMarks({ marks, inPart, labelOf = questionLabel }: {
  marks: StaffMarks; inPart: (key: string) => boolean; labelOf?: (key: string) => string;
}) {
  const here = [...marks.entries()].filter(([k]) => inPart(k));
  if (here.length === 0) return null;
  return (
    <ul data-testid="client-staff-marks" className="m-0 mb-5 list-none p-0 text-[12px] text-slate font-sans">
      {here.map(([key, at]) => (
        <li key={key} data-testid="client-staff-mark">{`${labelOf(key)}: ${clientStaffMark(markDate(at))}`}</li>
      ))}
    </ul>
  );
}
